/**
 * The API client. One function per verb, bearer token on every call, and
 * a typed error so a 409 on save can be shown as "reload and reapply"
 * rather than "something went wrong".
 */

import { authHeaders } from "./acting.js";
import type { Action, FeatureId, Ingredient, Item, MfaMethod, MfaRule, Recipe, Spec, SpecOverride } from "@baros/core";
import type { OrgSettings } from "./org/api.js";

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface Me {
  readonly userId: string;
  /** A Venox operator: sees the platform screen and may act in any org. */
  readonly platform: boolean;
  /** Every organisation this person belongs to; a switcher is offered past one. */
  readonly orgs: readonly { readonly orgId: string; readonly name: string }[];
  readonly org: {
    readonly orgId: string;
    readonly name: string;
    readonly role: string | null;
    /**
     * The org's settings as `/v1/me` returns them, which is the same settled
     * shape the settings route returns. The AI figures are already defaulted
     * server-side; the two lists come back empty when nobody has configured
     * them, and a screen chooses its own default for that.
     */
    readonly settings: OrgSettings;
  };
  readonly venues: readonly {
    readonly venueId: string;
    readonly name: string;
    readonly suburb?: string;
    readonly role: string | null;
    readonly assigned: boolean;
  }[];
  readonly can: Readonly<Record<Action, boolean>>;
  /** Whether this person has set a tablet PIN (#80). */
  readonly pinSet?: boolean;
  /** Set when the request came from an unlocked tablet. */
  readonly device?: { readonly deviceId: string; readonly venueId: string; readonly sessionId: string } | null;
  /**
   * Two-step sign-in (#92): what this person uses, whether that could be
   * confirmed with Cognito, the organisation's rule, and whether the rule
   * asks it of them.
   */
  /** The features this organisation has (D14); absent from an older API, when everything was on. */
  readonly features?: readonly FeatureId[];
  readonly mfa?: {
    readonly method: MfaMethod | null;
    readonly confirmed: boolean;
    readonly rule: MfaRule;
    readonly required: boolean;
  };
}

export interface Library {
  readonly org: { readonly orgId: string; readonly name?: string };
  readonly ingredients: readonly Ingredient[];
  readonly specs: readonly Spec[];
  /** Items and recipes (#180). The screens read `specs` until Phase B moves them. */
  readonly items?: readonly Item[];
  readonly recipes?: readonly Recipe[];
}

export interface VenueState {
  readonly venue: {
    readonly venueId: string;
    readonly name?: string;
    readonly suburb?: string;
    /**
     * An IANA zone on the venue's META, if one has been written. Nothing sets
     * it yet; `timezoneOf` in `@baros/core` falls back to `DEFAULT_TIMEZONE`,
     * and every screen that bounds a week or a month reads it through that.
     */
    readonly timezone?: string;
    /** The gross profit a drink is held to, percent; `gpTargetOf` settles it. */
    readonly gpTarget?: number;
  };
  readonly specOverrides: readonly SpecOverride[];
  readonly staff: readonly { readonly userId: string; readonly displayName: string; readonly role: string }[];
}

/**
 * What the app does about a token the API will not take.
 *
 * Registered once by `App`, because only it holds the session. Without it
 * an expired token was nobody's job: every screen caught its own 401 and
 * printed it, so a dashboard left open past the hour came back as eight
 * tiles reading "Token rejected: Token expired at …" with the person still
 * apparently signed in and no way on but a reload.
 */
export interface SessionWatcher {
  /**
   * Get a fresh token for a call the API refused, or null when there is
   * none to be had. Supplied by `App` rather than reached for here, which
   * is what keeps Cognito out of this module: `auth.ts` builds its pool on
   * import, so importing it from the client would drag the pool into every
   * screen's tests and into anything else that only wanted a fetch.
   */
  readonly renew: () => Promise<string | null>;
  /** The token is dead and could not be renewed. Sign out. */
  readonly onEnded: (token: string) => void;
}

let watcher: SessionWatcher | null = null;

export function watchSession(next: SessionWatcher | null): void {
  watcher = next;
}

/**
 * One refresh at a time.
 *
 * The dashboard fires eight calls at once and they expire together, so
 * without this an expired token meant eight parallel refreshes — seven of
 * them racing to replace a token the first had already replaced.
 */
let renewing: Promise<string | null> | null = null;

function renew(token: string): Promise<string | null> {
  // A tablet's unlock is ours, not Cognito's: there is nothing to refresh,
  // and the way back is the PIN screen, which `onEnded` knows about.
  if (token.startsWith("Device ")) return Promise.resolve(null);
  const ask = watcher?.renew;
  if (!ask) return Promise.resolve(null);
  renewing ??= ask().then(
    (fresh) => {
      renewing = null;
      return fresh;
    },
    () => {
      renewing = null;
      return null;
    },
  );
  return renewing;
}

function send(
  token: string,
  method: string,
  path: string,
  body: unknown,
): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    method,
    headers: {
      ...authHeaders(token),
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/**
 * For a caller that cannot use `request` because it does its own fetch —
 * the chat streams its answer. Hands back a token worth retrying with, or
 * null once the session is over, having ended it.
 */
export async function renewOrEnd(token: string): Promise<string | null> {
  const fresh = await renew(token);
  if (fresh) return fresh;
  watcher?.onEnded(token);
  return null;
}

/** Builds the error a caller's module throws; `ApiError` unless given. */
export type ApiFailure = (status: number, message: string) => Error;

/**
 * Every call to the API goes through here, so the answer to an expired
 * token is the same wherever it happens: refresh once and retry, and if
 * that will not do it, end the session rather than printing the refusal.
 */
export async function request<T>(
  token: string,
  method: "GET" | "PUT" | "POST" | "DELETE",
  path: string,
  body?: unknown,
  fail: ApiFailure = (status, message) => new ApiError(status, message),
): Promise<T> {
  let res = await send(token, method, path, body);

  // The five-minute timer in App refreshes ahead of expiry, but it cannot
  // run while a laptop is asleep or a tab is throttled, so a token does
  // still reach the API dead. One retry on a fresh one covers that.
  if (res.status === 401) {
    const fresh = await renew(token);
    if (fresh) res = await send(fresh, method, path, body);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    if (res.status === 401) watcher?.onEnded(token);
    throw fail(res.status, text || `${res.status} ${res.statusText}`);
  }
  // A 204 has no body to parse — `DELETE /v1/suppliers/…` answers with one.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface UsageCounter {
  readonly costCents: number;
  readonly turns: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
}

/**
 * The org's counter. `costCents` is everything charged against the cap;
 * the other two say how much of it was chat and how much the recipe shelf.
 */
export interface OrgUsageCounter extends UsageCounter {
  readonly usageCents: number;
  readonly storageCents: number;
  readonly storageBytes: number;
}

export interface UsageHistoryEntry {
  readonly period: string;
  readonly org: OrgUsageCounter;
  readonly venues: readonly (UsageCounter & { readonly venueId: string })[];
}

export interface Usage {
  readonly period: string;
  readonly org: OrgUsageCounter;
  readonly venues: readonly (UsageCounter & { readonly venueId: string })[];
  /** The last N periods asked for, newest first — `history[0]` is always this period. */
  readonly history: readonly UsageHistoryEntry[];
  readonly limits: { readonly aiMonthlyLimitCents: number | null; readonly aiPlanCeilingCents: number | null };
}

export const api = {
  me: (token: string) => request<Me>(token, "GET", "/v1/me"),
  /**
   * AI spend from the counters the chat writes. `months` (default 1, max 13)
   * adds that many periods of `history`, newest first.
   */
  usage: (token: string, months?: number) =>
    request<Usage>(token, "GET", `/v1/org/usage${months ? `?months=${months}` : ""}`),
  library: (token: string) => request<Library>(token, "GET", "/v1/library"),
  venueState: (token: string, venueId: string) =>
    request<VenueState>(token, "GET", `/v1/venues/${encodeURIComponent(venueId)}/state`),
  // Items and recipes (#180). A recipe's history and saves, as spec views.
  versions: (token: string, recipeId: string) =>
    request<{ versions: Spec[] }>(token, "GET", `/v1/recipes/${encodeURIComponent(recipeId)}/versions`),
  saveRecipe: (token: string, recipeId: string, body: unknown) =>
    request<{ recipe: Recipe; spec: Spec }>(token, "PUT", `/v1/recipes/${encodeURIComponent(recipeId)}`, body),
  saveItem: (token: string, itemId: string, body: unknown) =>
    request<{ item: Item }>(token, "PUT", `/v1/items/${encodeURIComponent(itemId)}`, body),
  createItem: (token: string, name: string, kind: "drink" | "food") =>
    request<{ item: Item; recipe: Recipe; spec: Spec }>(token, "POST", "/v1/items", { name, kind }),
  /** A variant under an item, copied from its House recipe. */
  createRecipe: (token: string, itemId: string, label: string) =>
    request<{ recipe: Recipe; spec: Spec }>(token, "POST", `/v1/items/${encodeURIComponent(itemId)}/recipes`, { label }),
  promoteRecipe: (token: string, recipeId: string) =>
    request<{ recipe: Recipe; demoted: Recipe | null }>(token, "POST", `/v1/recipes/${encodeURIComponent(recipeId)}/promote`, {}),
  saveIngredient: (token: string, ingredientId: string, body: unknown) =>
    request<{ ingredient: Ingredient }>(
      token,
      "PUT",
      `/v1/ingredients/${encodeURIComponent(ingredientId)}`,
      body,
    ),
  saveOverride: (token: string, venueId: string, specId: string, body: unknown) =>
    request<{ override: SpecOverride }>(
      token,
      "PUT",
      `/v1/venues/${encodeURIComponent(venueId)}/specs/${encodeURIComponent(specId)}`,
      body,
    ),
};
